import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Tag as TagIcon, Trash2, Plus, AlertCircle } from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { LinkTag } from "../../../types";
import Button from "../../../components/ui/Button";
import { useConfirm } from "../../../components/ui/ConfirmDialog";

// Shared with CampaignFormModal — a tag's color is picked from this set.
const COLORS = [
  "#6366f1", // brand
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // rose
  "#8b5cf6", // violet
  "#0ea5e9", // sky
  "#ec4899", // pink
  "#475569", // slate
];

interface Props {
  open: boolean;
  tags: LinkTag[];
  onClose: () => void;
  // Refetch the managed-tag list (and links, since renames/deletes touch them).
  onChanged: () => void | Promise<void>;
}

const inputCls =
  "flex-1 min-w-0 h-9 px-3 rounded-lg text-sm bg-white border border-ink-200 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500";

const ManageTagsModal = ({ open, tags, onClose, onChanged }: Props) => {
  const services = useAppServices();
  const confirm = useConfirm();
  // In-progress name edits, keyed by tag id.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [colorOpen, setColorOpen] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset local state whenever the modal opens or the tag list changes.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setColorOpen(null);
    setNewName("");
    setDrafts(Object.fromEntries(tags.map((t) => [t._id, t.name])));
  }, [open, tags]);

  if (!open) return null;

  const rename = async (tag: LinkTag) => {
    const name = (drafts[tag._id] ?? "").trim().toLowerCase();
    // No-op / blank → snap the field back to the saved name.
    if (!name || name === tag.name) {
      setDrafts((d) => ({ ...d, [tag._id]: tag.name }));
      return;
    }
    setBusy(true);
    setError(null);
    const { error: err } = await services.link_tags.update({
      payload: { _id: tag._id, name },
    });
    setBusy(false);
    if (err) {
      setError(err.message || "Couldn't rename that tag.");
      setDrafts((d) => ({ ...d, [tag._id]: tag.name }));
      return;
    }
    await onChanged();
  };

  const recolor = async (tag: LinkTag, color: string) => {
    setColorOpen(null);
    if (color === tag.color) return;
    setBusy(true);
    setError(null);
    const { error: err } = await services.link_tags.update({
      payload: { _id: tag._id, color },
    });
    setBusy(false);
    if (err) {
      setError(err.message || "Couldn't update the color.");
      return;
    }
    await onChanged();
  };

  const remove = async (tag: LinkTag) => {
    const ok = await confirm({
      title: `Delete "${tag.name}"?`,
      message: "This tag will be removed from all links that use it.",
      confirmLabel: "Delete tag",
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    const { error: err } = await services.link_tags.delete({
      query: `_id=${tag._id}`,
    });
    setBusy(false);
    if (err) {
      setError(err.message || "Couldn't delete that tag.");
      return;
    }
    await onChanged();
  };

  const add = async () => {
    const name = newName.trim().toLowerCase();
    if (!name) return;
    setBusy(true);
    setError(null);
    const { error: err } = await services.link_tags.create({
      payload: { name },
    });
    setBusy(false);
    if (err) {
      setError(err.message || "Couldn't add that tag.");
      return;
    }
    setNewName("");
    await onChanged();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 backdrop-blur-sm p-4 anim-in overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden my-auto">
        <div className="relative px-6 pt-6 pb-4 border-b border-ink-100">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm bg-brand-gradient">
              <TagIcon size={18} className="text-white" />
            </span>
            <div>
              <h2 className="font-display text-base font-semibold text-ink-900 tracking-tight">
                Manage tags
              </h2>
              <p className="text-xs text-ink-500 mt-0.5">
                Rename, recolor or delete tags — changes apply across all your
                links.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-ink-400 hover:text-ink-700 hover:bg-ink-100 w-8 h-8 rounded-lg flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-3">
              <AlertCircle size={15} className="shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {tags.length === 0 ? (
            <p className="text-sm text-ink-400 text-center py-6">
              No tags yet — add your first one below.
            </p>
          ) : (
            <div className="space-y-2">
              {tags.map((tag) => (
                <div key={tag._id} className="flex items-center gap-2">
                  <div className="relative shrink-0">
                    <button
                      type="button"
                      onClick={() =>
                        setColorOpen((c) => (c === tag._id ? null : tag._id))
                      }
                      className="w-6 h-6 rounded-full ring-1 ring-ink-200 hover:ring-ink-300"
                      style={{ background: tag.color || "#a1a1aa" }}
                      title="Change color"
                    />
                    {colorOpen === tag._id && (
                      <div className="absolute z-10 top-8 left-0 bg-white border border-ink-200 rounded-xl shadow-lg p-2 grid grid-cols-4 gap-1.5">
                        {COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => recolor(tag, c)}
                            className="w-6 h-6 rounded-full ring-1 ring-ink-200 hover:scale-110 transition-transform"
                            style={{ background: c }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    value={drafts[tag._id] ?? tag.name}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [tag._id]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        (e.target as HTMLInputElement).blur();
                      else if (e.key === "Escape")
                        setDrafts((d) => ({ ...d, [tag._id]: tag.name }));
                    }}
                    onBlur={() => rename(tag)}
                    disabled={busy}
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={() => remove(tag)}
                    disabled={busy}
                    className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-ink-400 hover:text-rose-600 hover:bg-rose-50"
                    title="Delete tag"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-ink-100">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") add();
              }}
              placeholder="New tag name"
              disabled={busy}
              className={inputCls}
            />
            <Button
              onClick={add}
              disabled={busy || !newName.trim()}
              icon={<Plus size={14} strokeWidth={2.5} />}
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ManageTagsModal;
